import React from 'react';
import { useSelector } from 'react-redux';
import 'AppSrc/core/style/components/tables/vf-table.css';
import { getSortValueForFullName } from 'AppSrc/core/utility-functions/sort-functions';
import {
  getTimingTextForValue,
  getNetworkTextForValue,
  getDateDisplay,
  getStageNameForStatus,
  getRowItem,
  getCrqListRowId,
  getCrqListRowKey,
} from 'AppSrc/core/crq/crq-table-utils';
import PropTypes from 'prop-types';
import { LoadingSpinner } from 'AppSrc/core/components/utility/loading-spinner';
import { PaginatedSortableListTable } from 'AppSrc/core/components/tables/paginated-sortable-list-table';
import { useHistory } from 'react-router';
import ReactTooltip from 'react-tooltip';
import { FilterPropsShape } from './crq-list-filters';
import { crqMatchesFilters } from './crq-list-filter-utils';





const CRQS_PER_PAGE = 20;

const columns = [
  {
    id: 'startDate',
    name: 'Start Date',
    sortable: true,
    sortWithNullLast: true,
  },
  { id: 'crqId', name: 'CRQ ID', sortable: true },
  { id: 'network', name: 'Network', sortable: true },
  { id: 'timing', name: 'SLA', sortable: true },
  { id: 'coordinatorName', name: 'Coordinator', sortable: true },
  { id: 'peerReviewerName', name: 'Peer', sortable: true },
  // { id: 'circuitCheckAnalyst', name: 'Change Analyst', sortable: true },
  { id: 'stage', name: 'Stage', sortable: true },
  { id: 'summary', name: 'Summary', sortable: true },
];

// Emergency L3 column change
const columnsEmergency = [
  {
    id: 'startDate',
    name: 'Start Date',
    sortable: true,
    sortWithNullLast: true,
  },
  { id: 'crqId', name: 'CRQ ID', sortable: true },
  { id: 'network', name: 'Network', sortable: true },
  { id: 'timing', name: 'SLA', sortable: true },
  { id: 'coordinatorName', name: 'Coordinator', sortable: true },
  { id: 'peerReviewerName', name: 'Peer/L3', sortable: true },
  // { id: 'circuitCheckAnalyst', name: 'Change Analyst', sortable: true },
  { id: 'stage', name: 'Stage', sortable: true },
  { id: 'summary', name: 'Summary', sortable: true },
];

const columnsForCircuitCheck = [
  {
    id: 'startDate',
    name: 'Start Date',
    sortable: true,
    sortWithNullLast: true,
  },
  { id: 'crqId', name: 'CRQ ID', sortable: true },
  { id: 'network', name: 'Network', sortable: true },
  { id: 'timing', name: 'SLA', sortable: true },
  { id: 'coordinatorName', name: 'Coordinator', sortable: true },
  { id: 'peerReviewerName', name: 'Peer', sortable: true },
  { id: 'circuitCheckAnalyst', name: 'Change Analyst', sortable: true },
  { id: 'stage', name: 'Stage', sortable: true },
  { id: 'summary', name: 'Summary', sortable: true },
];

const getRowForCrq = (crq, index) => {
  const {
    start_date: startDate,
    id: crqId,
    network,
    timing,
    coordinator_name: coordinatorName,
    peer_reviewer_name: peerReviewerName,
    // circuit_check_analyst: circuitCheckAnalyst,
    status,
    // eslint-disable-next-line camelcase
    status_reason,
    adjacent_remedy_ref: adjacent,
    summary,
  } = crq;

  // formating date
  const startDateDisplay = getDateDisplay(startDate);
  // Look up text values for id fields
  // Distinguish between Mobile CRQ and Mobile VINE CRQ in network column of HOME page table.
  let networkText = getNetworkTextForValue(network);
  const hasAdjRef = crq.hasOwnProperty('adjacent_remedy_ref');
  if (networkText === 'Mobile UK' && (adjacent !== null) && (hasAdjRef === true)) {
    networkText = 'Mobile(F)';
  }

  if (networkText === 'Mobile UK' && (adjacent === null)) {
    networkText = 'Mobile UK';
  }
  if (networkText === 'Fixed UK' && crq.summary.includes('#ICM_')) {
    console.log('iscoTest', networkText);
    networkText = 'International';
  }
  const timingText = getTimingTextForValue(network, `${timing}`);
  // Emergency check for peer to L3
  // eslint-disable-next-line camelcase
  let stage = (status_reason === '10500') ? 'Circuit Check' : getStageNameForStatus(status, network);
  if (timingText === 'Emergency' && stage === 'Peer Review') {
    stage = 'Peer/L3 Review';
  }


  // get sort value for the full names
  // sorting by last name first
  const coordinatorToSort = getSortValueForFullName(coordinatorName);
  const approverToSort = getSortValueForFullName(peerReviewerName);
  // const circuitAnalystToSort = getSortValueForFullName(circuitCheckAnalyst);
  // Added new column 'Summary' in the CRQs dashboard CRQ table to view CRQ summary on the dashboard.
  const isSummary = (
    <div className="column-summary dashboard-summary">
      <span className="row-item-text" data-tip={summary}>
        {summary}
      </span>
      <ReactTooltip />
    </div>
  );

  // create the array of cells
  const cells = [
    getRowItem({ columnId: 'startDate', displayText: startDateDisplay }),
    getRowItem({ columnId: 'crqId', displayText: crqId }),
    getRowItem({ columnId: 'network', displayText: networkText }),
    getRowItem({ columnId: 'timing', displayText: timingText }),
    getRowItem({ columnId: 'coordinatorName', displayText: coordinatorName }),
    getRowItem({ columnId: 'peerReviewerName', displayText: peerReviewerName }),
    // getRowItem({ columnId: 'circuitCheckAnalyst', displayText: circuitCheckAnalyst }),
    getRowItem({
      columnId: 'stage',
      displayText: stage,
      withChevron: true,
    }),
    getRowItem({ columnId: 'summary', displayText: isSummary }),
  ];

  // generate the values to sort by
  const sortValues = {
    startDate,
    crqId,
    network: networkText,
    timing: timingText,
    coordinatorName: coordinatorToSort,
    peerReviewerName: approverToSort,
    // circuitCheckAnalyst: circuitAnalystToSort,
    stage,
    adjacent,
    // summary: isSummary,
  };

  const rowId = getCrqListRowId(network, crqId);
  const rowKey = getCrqListRowKey(crqId, index);

  return ({
    id: rowId,
    key: rowKey,
    cells,
    sortValues,
  });
};

// This function is for Change Management group CRQs only, as this group has additional Change Analyst column.
const getRowForChangeManagementCrq = (crq, index) => {
  const {
    start_date: startDate,
    id: crqId,
    network,
    timing,
    coordinator_name: coordinatorName,
    peer_reviewer_name: peerReviewerName,
    circuit_check_analyst: circuitCheckAnalyst,
    status,
    // eslint-disable-next-line camelcase
    status_reason,
    adjacent_remedy_ref: adjacent,
    summary,
  } = crq;

  // formating date
  const startDateDisplay = getDateDisplay(startDate);
  // Look up text values for id fields
  // Distinguish between Mobile CRQ and Mobile VINE CRQ in network column of HOME page table.
  let networkText = getNetworkTextForValue(network);
  const hasAdjRef = crq.hasOwnProperty('adjacent_remedy_ref');
  if (networkText === 'Mobile' && (adjacent !== null) && (hasAdjRef === true)) {
    networkText = 'Mobile(F)';
  }

  if (networkText === 'Mobile' && (adjacent === null)) {
    networkText = 'Mobile';
  }
  if (networkText === 'Fixed UK' && crq.summary.includes('#ICM_')) {
    console.log('iscoTest', networkText);
    networkText = 'International';
  }
  const timingText = getTimingTextForValue(network, `${timing}`);
  // eslint-disable-next-line camelcase
  const stage = (status_reason === '10500') ? 'Circuit Check' : getStageNameForStatus(status, network);
  console.log('iscome', stage, crq.summary.includes('#ICM_'), networkText);


  // get sort value for the full names
  // sorting by last name first
  const coordinatorToSort = getSortValueForFullName(coordinatorName);
  const approverToSort = getSortValueForFullName(peerReviewerName);
  const circuitAnalystToSort = getSortValueForFullName(circuitCheckAnalyst);
  // Added new column 'Summary' in the Change management CRQs table to view CRQ summary on the dashboard.
  const isSummary = (
    <div className="column-summary dashboard-summary">
      <span className="row-item-text" data-tip={summary}>
        {summary}
      </span>
      <ReactTooltip />
    </div>
  );

  // create the array of cells
  const cells = [
    getRowItem({ columnId: 'startDate', displayText: startDateDisplay }),
    getRowItem({ columnId: 'crqId', displayText: crqId }),
    getRowItem({ columnId: 'network', displayText: networkText }),
    getRowItem({ columnId: 'timing', displayText: timingText }),
    getRowItem({ columnId: 'coordinatorName', displayText: coordinatorName }),
    getRowItem({ columnId: 'peerReviewerName', displayText: peerReviewerName }),
    getRowItem({ columnId: 'circuitCheckAnalyst', displayText: circuitCheckAnalyst }),
    getRowItem({
      columnId: 'stage',
      displayText: stage,
      withChevron: true,
    }),
    getRowItem({ columnId: 'summary', displayText: isSummary }),
  ];

  // generate the values to sort by
  const sortValues = {
    startDate,
    crqId,
    network: networkText,
    timing: timingText,
    coordinatorName: coordinatorToSort,
    peerReviewerName: approverToSort,
    circuitCheckAnalyst: circuitAnalystToSort,
    stage,
    adjacent,
  };

  const rowId = getCrqListRowId(network, crqId);
  const rowKey = getCrqListRowKey(crqId, index);

  return ({
    id: rowId,
    key: rowKey,
    cells,
    sortValues,
  });
};

const CrqListTable = ({
  crqList,
  fetching,
  noResultsMessage,
  filters,
  coordinatorGroupName,
  isDuplicate,
}) => {

  const dataNew = useSelector((state) => state.auth.user);
  console.log('new', dataNew);
  const history = useHistory();
  if (fetching || !crqList) {
    return (
      <div className="crq-table-loader">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredList = crqList.filter((crq) => crqMatchesFilters(crq, filters));
  const isEmergencyCrqList = crqList.filter((value) => value.timing.includes('2000', 'emergency'));
  const isEmergency = isEmergencyCrqList.length === 0 ? false : true;
  const finalcolumn = isEmergency ? columnsEmergency : columns;
  const rows = filteredList.map(getRowForCrq);
  const rowsForChangeManagement = filteredList.map(getRowForChangeManagementCrq);

  const rowsPerPage = CRQS_PER_PAGE;
  return (
    <div className="crq-table">
      <PaginatedSortableListTable
        rows={coordinatorGroupName === 'Change Management' ? rowsForChangeManagement : rows}
        columns={coordinatorGroupName === 'Change Management' ? columnsForCircuitCheck : finalcolumn}
        rowsPerPage={rowsPerPage}
        onClickRow={(rowId) => history.push(`/crq/${rowId}`)} // rowId is E.G 'mobile/CRQ-000001'
        className="crq-list-table"
        initialSortedColumn="startDate"
        initialSortDesc={false}
        noResultsMessage={noResultsMessage}
        isDuplicate={isDuplicate}
      />
    </div>
  );
};

CrqListTable.propTypes = {
  crqList: PropTypes.arrayOf(PropTypes.object),
  fetching: PropTypes.bool,
  noResultsMessage: PropTypes.string,
  filters: FilterPropsShape,
  coordinatorGroupName: PropTypes.string,
  isDuplicate: PropTypes.bool,
};

export { CrqListTable };
